import { router } from '@inertiajs/react'
import { Folder, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { LucideIcon } from '@/lib/lucideIconRegistry'
import { cn } from '@/lib/utils'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { HelpCategoryNode } from '@/pages/HelpCenter/types'

interface HelpCategoryTreeProps {
  categories: HelpCategoryNode[]
  selectedId: string | null
  onSelect: (categoryId: string) => void
  onAdd: () => void
  onEdit: (category: HelpCategoryNode) => void
}

export function HelpCategoryTree({ categories, selectedId, onSelect, onAdd, onEdit }: HelpCategoryTreeProps) {
  const { withAccount } = useRouteParams()
  const roots = categories.filter(c => !c.parent_id)
  const childrenOf = (id: string) => categories.filter(c => c.parent_id === id)

  const remove = (category: HelpCategoryNode) => {
    router.delete(routes.helpCategories.destroy.path(withAccount({ id: category.slug })), { preserveScroll: true })
  }

  const renderNode = (category: HelpCategoryNode, depth: number) => (
    <div key={category.id}>
      <div
        role="button"
        tabIndex={0}
        aria-pressed={selectedId === category.id}
        onClick={() => onSelect(category.id)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(category.id) } }}
        className={cn(
          'group flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          selectedId === category.id && 'bg-muted text-foreground',
        )}
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        {!category.icon ? (
          <Folder className="size-4 shrink-0 text-muted-foreground" />
        ) : /^[a-z0-9-]+$/.test(category.icon) ? (
          <LucideIcon name={category.icon} className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <span className="shrink-0 text-sm leading-none">{category.icon}</span>
        )}
        <span className="truncate text-sm">{category.name}</span>
        {!category.published && <Badge variant="secondary" className="ml-1">Draft</Badge>}
        <div className="ml-auto flex items-center gap-0.5 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="size-7" onClick={() => onEdit(category)} aria-label="Edit category">
            <Pencil className="size-3.5" />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" className="size-7" aria-label="Delete category">
                <Trash2 className="size-3.5" />
              </Button>
            }
            title={`Delete "${category.name}"?`}
            description="Its articles become uncategorized and any sub-categories move up a level. This can't be undone."
            confirmText="Delete"
            destructive
            onConfirm={() => remove(category)}
          />
        </div>
      </div>
      {childrenOf(category.id).map(child => renderNode(child, depth + 1))}
    </div>
  )

  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="text-sm font-medium">Categories</h3>
        <Button variant="ghost" size="icon" className="size-7" onClick={onAdd} aria-label="Add category">
          <Plus className="size-4" />
        </Button>
      </div>
      <div className="p-1.5">
        {roots.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">No categories yet.</p>
        ) : (
          roots.map(root => renderNode(root, 0))
        )}
      </div>
    </div>
  )
}
